// TESSERAX — ARBITRAGE DU FEEDBACK (cœur pur, testé).
//
// Le jeu a accumulé, passe après passe, une dizaine de canaux de célébration (noms
// d'effacement, Croix, Continuum, dégagement, jalons, chasse au record, vrille, cascade,
// Zone…), chacun ajouté ISOLÉMENT avec son bandeau / son toast. Pris ensemble, ils
// peuvent CRIER en même temps : deux feats coïncidents écrasaient mutuellement le toast
// (canal à fente unique → le 1er disparaît avant d'être lu), et un petit feat pouvait
// stomper le bandeau d'un grand. Quand TOUT est célébré, plus RIEN ne ressort.
//
// On centralise donc ici la DÉCISION de priorité (quel feat tient le devant de la scène),
// sans toucher au juice : un seul barème ordonne les évènements ; main.ts s'en sert pour
// (1) n'écraser un bandeau que par un feat de rang ≥ tant que le précédent « tient », et
// (2) FILER les toasts coïncidents au lieu de les écraser (file priorisée). Le rare
// redevient rare. AFFICHAGE seul → aucun score, aucun RNG touché, déterminisme intact.
//
// Module PUR : ni THREE ni DOM, pas de Math.random, pas d'I/O.

// Barème de priorité (plus grand = plus important). Échelle volontairement creusée pour
// laisser de la place à des ajouts futurs entre deux paliers. Les feats RARES et à fort
// signal (parfait, relâche de Zone en banque, dégagement, singularité de cascade) trônent ;
// la respiration de base (ligne, info) reste au plancher. Sert AUSSI bien au bandeau central
// (bigText) qu'au canal toast (les deux puisent dans le même ordre de mérite).
export const FEEDBACK_RANK: Record<string, number> = {
  perfect: 100,        // puits entièrement vidé — l'apogée
  zonebank: 92,        // relâche d'une Zone mise en banque
  hero: 80,            // 1er effacement de la session (moment « héros »)
  clutch: 76,          // dégagement in extremis (sortie du rouge)
  cascade: 70,         // cascade nommée (CASCADE / DÉFERLEMENT / SINGULARITÉ) — l'apogée préparable
  bascule: 66,         // effacement par Bascule (garde son bandeau face à son propre combo/grille…
  grille: 62,          // …mais cède à la cascade nommée ≥3 → la grosse Bascule montre son apogée)
  cross: 58,           // Croix (deux axes, feat 3D) — surfacée via le canal toast
  milestone: 52,       // jalon de score (grand nombre rond)
  spin: 50,            // effacement par vrille
  recordlive: 48,      // record du mode battu en direct
  line: 44,            // double / triple / quadruple / faille « à plat »
  combo: 40,           // cascade courte (×2) — la respiration enchaînée
  continuum: 38,       // back-to-back (maîtrise enchaînée)
  cascadeReady: 30,    // INVITATION : une grosse cascade est prête (pré-évènement)
  mission: 26,         // mission accomplie
  info: 12,            // astuces / état (Zone prête, Bascule dispo, peek, manette…)
};
// Rang par défaut d'un évènement non catalogué (entre `mission` et `info`) : un appelant
// historique qui ne précise pas de `kind` reste pris en charge sans privilège ni famine.
export const DEFAULT_RANK = 20;

export function rankOf(kind?: string): number {
  if (!kind) return DEFAULT_RANK;
  const r = FEEDBACK_RANK[kind];
  return typeof r === 'number' ? r : DEFAULT_RANK;
}

// BANDEAU : un nouveau bandeau (newRank) doit-il remplacer celui affiché (curRank) ? Un rang
// SUPÉRIEUR OU ÉGAL gagne toujours (le feat le plus récent et au moins aussi fort prime) ; un
// rang INFÉRIEUR doit attendre que le bandeau courant ait « tenu » sa fenêtre de lecture
// (now ≥ heldUntil) → un petit feat ne vole plus la vedette d'un grand juste après lui.
export function bannerWins(curRank: number, newRank: number, now: number, heldUntil: number): boolean {
  if (newRank >= curRank) return true;
  return now >= heldUntil;
}

// TOAST : index du prochain toast à montrer dans une file en attente — le plus haut rang
// d'abord, à égalité le plus ANCIEN (FIFO → ordre d'arrivée préservé entre égaux). Renvoie -1
// pour une file vide. Pur : ne mute pas la file (main.ts retire l'élément après lecture).
export function pickToast(queue: ReadonlyArray<{ rank: number; seq: number }>): number {
  let best = -1;
  for (let i = 0; i < queue.length; i++) {
    if (best === -1 || queue[i].rank > queue[best].rank ||
        (queue[i].rank === queue[best].rank && queue[i].seq < queue[best].seq)) best = i;
  }
  return best;
}
